import { api } from "@/shared/lib/api";

export const FriendCategory = {
  None: 0,
  Teammate: 1,
  Acquaintance: 2,
  Favorite: 3,
} as const;

export type FriendCategoryValue = (typeof FriendCategory)[keyof typeof FriendCategory];

export interface FriendListItem {
  id: string;
  username: string;
  displayName: string;
  avatarUrl?: string | null;
  hasPro?: boolean;
  proNicknameStyle?: number;
  proNicknameColorA?: string | null;
  proNicknameColorB?: string | null;
  note?: string | null;
  category: number;
  status?: string | null;
  activityContext?: string | null;
  /** KAN-1356: ранг основного Riot-аккаунта («GOLD II»), null — не привязан. */
  rank?: string | null;
  /** Роли из активной карточки «Ищу команду»; пусто — карточки нет, роль обсуждается самими. */
  lfgRoles?: string[];
}

export interface FriendRequestListItem {
  id: string;
  requesterId: string;
  requesterUsername: string;
  requesterDisplayName: string;
  requesterAvatarUrl?: string | null;
  requesterHasPro?: boolean;
  requesterProNicknameStyle?: number;
  requesterProNicknameColorA?: string | null;
  requesterProNicknameColorB?: string | null;
  status: string;
  createdAt: string;
}

export interface FriendRequestStatusResult {
  isFriend: boolean;
  pendingOutgoing: boolean;
  pendingIncoming: boolean;
  pendingRequestId?: string | null;
}

export interface FriendPresenceItem {
  userId: string;
  status: string;
  activityContext?: string | null;
  lastSeenAt?: string | null;
}

const FRIENDS_BASE = "/friends";

export async function getFriends(): Promise<FriendListItem[]> {
  const { data } = await api.get<FriendListItem[]>(FRIENDS_BASE);
  return Array.isArray(data) ? data : [];
}

export async function getFriendTeammateIds(): Promise<string[]> {
  const { data } = await api.get<string[]>(`${FRIENDS_BASE}/teammate-ids`);
  return Array.isArray(data) ? data : [];
}

export async function getFriendRequests(): Promise<FriendRequestListItem[]> {
  const { data } = await api.get<FriendRequestListItem[]>(`${FRIENDS_BASE}/requests`);
  return Array.isArray(data) ? data : [];
}

export async function getFriendStatus(otherUserId: string): Promise<FriendRequestStatusResult> {
  const { data } = await api.get<FriendRequestStatusResult>(`${FRIENDS_BASE}/status/${otherUserId}`);
  return data ?? { isFriend: false, pendingOutgoing: false, pendingIncoming: false };
}

export async function getFriendsOnline(): Promise<FriendPresenceItem[]> {
  const { data } = await api.get<FriendPresenceItem[]>(`${FRIENDS_BASE}/online`);
  return Array.isArray(data) ? data : [];
}

export async function sendFriendRequest(addresseeUserId: string): Promise<{ id: string }> {
  const { data } = await api.post<{ id: string }>(`${FRIENDS_BASE}/request`, {
    addresseeUserId,
  });
  return data as { id: string };
}

export async function acceptFriendRequest(requestId: string): Promise<void> {
  await api.post(`${FRIENDS_BASE}/requests/${requestId}/accept`);
}

export async function declineFriendRequest(requestId: string): Promise<void> {
  await api.post(`${FRIENDS_BASE}/requests/${requestId}/decline`);
}

export async function cancelFriendRequest(requestId: string): Promise<void> {
  await api.post(`${FRIENDS_BASE}/requests/${requestId}/cancel`);
}

export async function removeFriend(friendId: string): Promise<void> {
  await api.delete(`${FRIENDS_BASE}/${friendId}`);
}

export async function updateFriendNote(friendId: string, note: string | null): Promise<void> {
  await api.put(`${FRIENDS_BASE}/${friendId}/note`, { note: note ?? "" });
}

export async function updateFriendCategory(friendId: string, category: number): Promise<void> {
  await api.put(`${FRIENDS_BASE}/${friendId}/category`, { category });
}

export type TeamRole =
  | "Top"
  | "Jungle"
  | "Mid"
  | "Adc"
  | "Support"
  | "Substitute"
  | "Coach";

export async function inviteFriendToTeam(
  friendId: string,
  teamId: string,
  role?: TeamRole
): Promise<{ id: string }> {
  const { data } = await api.post<{ id: string }>(
    `${FRIENDS_BASE}/${friendId}/invite-to-team`,
    { teamId, role }
  );
  return data as { id: string };
}

export interface PlayerComparisonSide {
  userId: string;
  displayName: string;
  avatarUrl?: string | null;
  hasPro?: boolean;
  proNicknameStyle?: number;
  proNicknameColorA?: string | null;
  proNicknameColorB?: string | null;
  totalGames: number;
  winrate: number;
  avgKills: number;
  avgDeaths: number;
  avgAssists: number;
  avgCs: number;
  avgDamage: number;
  avgGold: number;
  avgVisionScore: number;
  achievementsCount: number;
}

export interface PlayerComparisonResponse {
  player1: PlayerComparisonSide;
  player2: PlayerComparisonSide;
}

export async function compareWithFriend(myUserId: string, friendUserId: string): Promise<PlayerComparisonResponse> {
  const { data } = await api.get<PlayerComparisonResponse>(
    `/user-stats/${myUserId}/compare/${friendUserId}`,
  );
  return data;
}

export interface TournamentFriendsResponse {
  tournamentId: string;
  friends: FriendListItem[];
  count: number;
}

export interface TournamentWithFriendsItem {
  tournamentId: string;
  friendsCount: number;
  friends: FriendListItem[];
}

export async function getTournamentFriends(tournamentId: string): Promise<TournamentFriendsResponse> {
  const { data } = await api.get<TournamentFriendsResponse>(`/tournaments/${tournamentId}/friends`);
  return data;
}

export async function getTournamentsWithFriends(): Promise<TournamentWithFriendsItem[]> {
  const { data } = await api.get<TournamentWithFriendsItem[]>("/tournaments/with-friends");
  return Array.isArray(data) ? data : [];
}

export interface FriendActivityItem {
  id: string;
  userId: string;
  displayName: string;
  avatarUrl?: string | null;
  hasPro?: boolean;
  proNicknameStyle?: number;
  proNicknameColorA?: string | null;
  proNicknameColorB?: string | null;
  activityType: string;
  title: string;
  description?: string | null;
  relatedEntityId?: string | null;
  relatedEntityType?: string | null;
  createdAt: string;
}

export interface FriendActivityFeedResponse {
  items: FriendActivityItem[];
  totalCount: number;
}

export async function getFriendsActivity(page = 1, pageSize = 20): Promise<FriendActivityFeedResponse> {
  const { data } = await api.get<FriendActivityFeedResponse>(`${FRIENDS_BASE}/activity`, {
    params: { page, pageSize },
  });
  return data ?? { items: [], totalCount: 0 };
}
